import {
  criterionScoreFraction,
  customCriteriaAverage,
  formatCriterionScore,
} from "@shared/custom-feedback-format";
import type {
  Applicant,
  Application,
  Interview,
  InterviewStage,
  JobOpening,
  ReferenceCheck,
} from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Brain,
  Briefcase,
  CheckCircle,
  ClipboardList,
  DollarSign,
  Eye,
  FileText,
  Heart,
  Linkedin,
  Loader2,
  MessageSquare,
  Phone,
  RefreshCw,
  Star,
  User,
  Users,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { useParams } from "wouter";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/constants";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

type InterviewWithDetails = Interview & {
  interviewer: { id: string; fullName: string; schedulingLink: string | null } | null;
  stage: { id: string; stageName: string; stageOrder: number; materials: string | null } | null;
};

type ApplicationDetails = Application & {
  applicant?: Applicant;
  jobOpening?: JobOpening;
  interviews: InterviewWithDetails[];
  interviewStages: InterviewStage[];
  dossierDecidedByEmployee?: { id: string; fullName: string } | null;
  referenceChecks?: ReferenceCheck[];
  canDecideDossier?: boolean;
};

function VerdictDisplay({ verdict }: { verdict: string | null }) {
  if (!verdict) return <span className="text-[hsl(var(--ink-muted))]">-</span>;
  if (verdict === "YES")
    return <span className="text-[13px] font-semibold text-[hsl(var(--forest-pulse))]">Sim</span>;
  if (verdict === "NO")
    return <span className="text-[13px] font-semibold text-[hsl(var(--ink-muted))]">Não</span>;
  return <span className="text-[13px] font-semibold text-[hsl(var(--ink-muted))]">N/A</span>;
}

function getAverageScore(interview: InterviewWithDetails): string {
  const customAvg = customCriteriaAverage(interview.customCriteriaScores ?? []);
  if (customAvg !== null) return `${(customAvg * 100).toFixed(0)}%`;
  const verdicts = [
    interview.gcaVerdict,
    interview.roleKnowledgeVerdict,
    interview.compWayVerdict,
  ].filter((v): v is string => v !== null && v !== undefined && v !== "UNKNOWN");
  if (verdicts.length === 0) return "-";
  const yesCount = verdicts.filter((v) => v === "YES").length;
  return `${((yesCount / verdicts.length) * 100).toFixed(0)}%`;
}

function _getRecommendationLabel(rec: string | null): string {
  if (!rec) return "-";
  if (rec === "advance") return "Avançar";
  if (rec === "reject") return "Rejeitar";
  return rec;
}

export default function DossierPage() {
  const params = useParams<{ applicationId: string }>();
  const applicationId = params.applicationId;

  const { toast } = useToast();

  const {
    data: details,
    isLoading,
    error,
  } = useQuery<ApplicationDetails>({
    queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } }).queryKey,
    queryFn: () => rpcClient.applications.getDetails({ id: applicationId! }),
    enabled: !!applicationId,
  });

  const approveMutation = useMutation({
    mutationFn: () => rpcClient.applications.approveDossier({ id: applicationId! }),
    onSuccess: () => {
      toast({
        title: "Dossiê aprovado",
        description: "O candidato foi movido para a etapa de Oferta.",
      });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
    },
    onError: (err: any) => {
      const message = err?.message || "Não foi possível aprovar o dossiê.";
      toast({ title: "Erro ao aprovar", description: message, variant: "destructive" });
    },
  });

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [reevaluateDialogOpen, setReevaluateDialogOpen] = useState(false);
  const [reevaluateDecision, setReevaluateDecision] = useState<string>("");
  const [reevaluateReason, setReevaluateReason] = useState("");

  const [viewingRc, setViewingRc] = useState<ReferenceCheck | null>(null);

  const reevaluateApproveMutation = useMutation({
    mutationFn: () => rpcClient.applications.approveDossier({ id: applicationId! }),
    onSuccess: () => {
      toast({
        title: "Dossiê aprovado",
        description: "O candidato foi movido para a etapa de Oferta.",
      });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setReevaluateDialogOpen(false);
      setReevaluateDecision("");
      setReevaluateReason("");
    },
    onError: (err: any) => {
      const message = err?.message || "Não foi possível aprovar o dossiê.";
      toast({ title: "Erro ao aprovar", description: message, variant: "destructive" });
    },
  });

  const reevaluateRejectMutation = useMutation({
    mutationFn: (reason: string) =>
      rpcClient.applications.rejectDossier({ id: applicationId!, reason }),
    onSuccess: () => {
      toast({ title: "Dossiê recusado", description: "O candidato foi movido para Recusado." });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setReevaluateDialogOpen(false);
      setReevaluateDecision("");
      setReevaluateReason("");
    },
    onError: (err: any) => {
      const message = err?.message || "Não foi possível rejeitar o dossiê.";
      toast({ title: "Erro ao rejeitar", description: message, variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) =>
      rpcClient.applications.rejectDossier({ id: applicationId!, reason }),
    onSuccess: () => {
      toast({ title: "Dossiê recusado", description: "O candidato foi movido para Recusado." });
      queryClient.invalidateQueries({
        queryKey: orpc.applications.getDetails.queryOptions({ input: { id: applicationId! } })
          .queryKey,
      });
      setRejectDialogOpen(false);
      setRejectReason("");
    },
    onError: (err: any) => {
      const message = err?.message || "Não foi possível rejeitar o dossiê.";
      toast({ title: "Erro ao rejeitar", description: message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-8 space-y-6" data-testid="dossier-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="max-w-4xl mx-auto py-8" data-testid="dossier-error">
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">Dossiê não encontrado</h2>
            <p className="text-muted-foreground">
              Não foi possível carregar os dados do dossiê para esta candidatura.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const completedInterviews = details.interviews.filter(
    (i) =>
      i.completedAt &&
      (i.gcaVerdict !== null ||
        i.roleKnowledgeVerdict !== null ||
        i.compWayVerdict !== null ||
        (i.customCriteriaScores?.length ?? 0) > 0),
  );

  const overallAverages = (() => {
    const gcaVerdicts = completedInterviews
      .map((i) => i.gcaVerdict)
      .filter((v): v is string => v !== null && v !== undefined && v !== "UNKNOWN");
    const rkVerdicts = completedInterviews
      .map((i) => i.roleKnowledgeVerdict)
      .filter((v): v is string => v !== null && v !== undefined && v !== "UNKNOWN");
    const cwVerdicts = completedInterviews
      .map((i) => i.compWayVerdict)
      .filter((v): v is string => v !== null && v !== undefined && v !== "UNKNOWN");

    const avgGca =
      gcaVerdicts.length > 0
        ? gcaVerdicts.filter((v) => v === "YES").length / gcaVerdicts.length
        : null;
    const avgRk =
      rkVerdicts.length > 0
        ? rkVerdicts.filter((v) => v === "YES").length / rkVerdicts.length
        : null;
    const avgCw =
      cwVerdicts.length > 0
        ? cwVerdicts.filter((v) => v === "YES").length / cwVerdicts.length
        : null;

    // Overall total mixes both form types: default verdicts contribute YES=1 /
    // NO=0 and each custom criterion contributes its normalized fraction.
    const allVerdicts = [...gcaVerdicts, ...rkVerdicts, ...cwVerdicts];
    const verdictFractions = allVerdicts.map((v) => (v === "YES" ? 1 : 0));
    const customFractions = completedInterviews.flatMap((i) =>
      (i.customCriteriaScores ?? []).map(criterionScoreFraction),
    );
    const allFractions = [...verdictFractions, ...customFractions];
    const avgTotal =
      allFractions.length > 0
        ? allFractions.reduce((sum, f) => sum + f, 0) / allFractions.length
        : null;

    return { avgGca, avgRk, avgCw, avgTotal };
  })();

  const _statusLabel: Record<string, string> = {
    applied: "Inscrito",
    prioritized: "Priorizado",
    deprioritized: "Despriorizado",
    interviewing: "Em Entrevista",
    reference_checks: "Reference Checks",
    dossier: "Committee",
    offer: "Oferta",
    hired: "Contratado",
    rejected: "Rejeitado",
    offer_rejected: "Offer Rejected",
  };

  const hiringManagerName = (details.jobOpening as any)?.hiringManager?.fullName || "-";

  const isDossierStage = details.status === "dossier";

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="dossier-page">
      <PageHeader title="Dossiê do Candidato" titleTestId="text-dossier-title">
        {isDossierStage && !details.dossierDecision && details.canDecideDossier && (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => setRejectDialogOpen(true)}
              disabled={rejectMutation.isPending}
              data-testid="button-reject-dossier"
            >
              {rejectMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4 mr-2" />
              )}
              Recusar
            </Button>
            <Button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
              data-testid="button-approve-dossier"
            >
              {approveMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Aprovar
            </Button>
          </div>
        )}
        {isDossierStage && details.dossierDecision && details.canDecideDossier && (
          <Button
            variant="outline"
            onClick={() => setReevaluateDialogOpen(true)}
            data-testid="button-reevaluate-dossier"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reavaliar
          </Button>
        )}
      </PageHeader>

      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent data-testid="dialog-reject-dossier">
          <DialogHeader>
            <DialogTitle>Recusar dossiê</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Informe a justificativa para a recusa do dossiê. Esta informação será compartilhada
              com o Hiring Manager.
            </p>
            <Textarea
              placeholder="Justificativa da recusa..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              data-testid="textarea-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              data-testid="button-cancel-reject"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => rejectMutation.mutate(rejectReason)}
              disabled={rejectMutation.isPending || rejectReason.trim().length === 0}
              data-testid="button-confirm-reject"
            >
              {rejectMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Recusar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={reevaluateDialogOpen}
        onOpenChange={(open) => {
          setReevaluateDialogOpen(open);
          if (!open) {
            setReevaluateDecision("");
            setReevaluateReason("");
          }
        }}
      >
        <DialogContent data-testid="dialog-reevaluate-dossier">
          <DialogHeader>
            <DialogTitle>Reavaliar dossiê</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Escolha a nova decisão para este dossiê. A decisão anterior será substituída.
            </p>
            <div className="space-y-2">
              <Label>Decisão</Label>
              <Select value={reevaluateDecision} onValueChange={setReevaluateDecision}>
                <SelectTrigger data-testid="select-reevaluate-decision">
                  <SelectValue placeholder="Selecione a decisão" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Aprovar</SelectItem>
                  <SelectItem value="rejected">Recusar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {reevaluateDecision === "rejected" && (
              <div className="space-y-2">
                <Label>Justificativa</Label>
                <Textarea
                  placeholder="Justificativa da recusa..."
                  value={reevaluateReason}
                  onChange={(e) => setReevaluateReason(e.target.value)}
                  rows={4}
                  data-testid="textarea-reevaluate-reason"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReevaluateDialogOpen(false)}
              data-testid="button-cancel-reevaluate"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (reevaluateDecision === "approved") {
                  reevaluateApproveMutation.mutate();
                } else if (reevaluateDecision === "rejected") {
                  reevaluateRejectMutation.mutate(reevaluateReason);
                }
              }}
              disabled={
                !reevaluateDecision ||
                (reevaluateDecision === "rejected" && reevaluateReason.trim().length === 0) ||
                reevaluateApproveMutation.isPending ||
                reevaluateRejectMutation.isPending
              }
              data-testid="button-confirm-reevaluate"
            >
              {(reevaluateApproveMutation.isPending || reevaluateRejectMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {details.dossierDecision && (
        <Card data-testid="card-dossier-decision">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="w-4 h-4" />
              Decisão do Dossiê
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {details.dossierDecision === "approved" ? (
                  <span
                    className="inline-flex items-center gap-1 text-[13px] font-semibold text-[hsl(var(--forest-pulse))]"
                    data-testid="badge-decision-approved"
                  >
                    <CheckCircle className="w-3 h-3" />
                    Aprovado
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 text-[13px] font-semibold text-[hsl(var(--ink-muted))]"
                    data-testid="badge-decision-rejected"
                  >
                    <XCircle className="w-3 h-3" />
                    Recusado
                  </span>
                )}
              </div>
              {details.dossierDecisionReason && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Justificativa</p>
                  <p className="text-sm" data-testid="text-decision-reason">
                    {details.dossierDecisionReason}
                  </p>
                </div>
              )}
              {details.dossierDecidedByEmployee && (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Decidido por</p>
                  <p
                    className="text-sm font-medium flex items-center gap-1.5"
                    data-testid="text-decided-by"
                  >
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                    {details.dossierDecidedByEmployee.fullName}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card data-testid="card-dossier-header">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl font-semibold" data-testid="text-candidate-name">
                  {details.applicant?.fullName || "Candidato"}
                </h2>
                <StatusBadge status={details.status} />
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground flex-wrap">
                <span className="flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4" />
                  <span data-testid="text-job-title">{details.jobOpening?.title || "-"}</span>
                </span>
                {details.applicant?.email && (
                  <span className="flex items-center gap-1.5" data-testid="text-candidate-email">
                    {details.applicant.email}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-summary-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Star className="w-4 h-4" />
            Resumo das Avaliações
          </CardTitle>
        </CardHeader>
        <CardContent>
          {completedInterviews.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="text-no-interviews">
              Nenhuma entrevista com avaliação disponível.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="table-summary">
                <TableHeader>
                  <TableRow>
                    <TableHead>Entrevistador</TableHead>
                    <TableHead>Etapa</TableHead>
                    <TableHead className="text-center">GCA</TableHead>
                    <TableHead className="text-center">Role Knowledge</TableHead>
                    <TableHead className="text-center">COMP Way</TableHead>
                    <TableHead className="text-center">Média</TableHead>
                    <TableHead className="text-center">Recomendação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {completedInterviews
                    .sort((a, b) => (a.stage?.stageOrder || 0) - (b.stage?.stageOrder || 0))
                    .map((interview) => (
                      <TableRow key={interview.id} data-testid={`row-interviewer-${interview.id}`}>
                        <TableCell className="font-medium">
                          {interview.interviewer?.fullName || "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {interview.stage?.stageName || "-"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <VerdictDisplay verdict={interview.gcaVerdict} />
                        </TableCell>
                        <TableCell className="text-center">
                          <VerdictDisplay verdict={interview.roleKnowledgeVerdict} />
                        </TableCell>
                        <TableCell className="text-center">
                          <VerdictDisplay verdict={interview.compWayVerdict} />
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {getAverageScore(interview)}
                        </TableCell>
                        <TableCell className="text-center">
                          {interview.recommendation === "advance" ? (
                            <Badge
                              variant="outline"
                              className="border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400 text-xs"
                            >
                              Avançar
                            </Badge>
                          ) : interview.recommendation === "reject" ? (
                            <Badge
                              variant="outline"
                              className="border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400 text-xs"
                            >
                              Rejeitar
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  <TableRow
                    className="bg-muted/50 font-semibold"
                    data-testid="row-overall-averages"
                  >
                    <TableCell colSpan={2}>Média Geral</TableCell>
                    <TableCell className="text-center">
                      {overallAverages.avgGca !== null
                        ? `${(overallAverages.avgGca * 100).toFixed(0)}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {overallAverages.avgRk !== null
                        ? `${(overallAverages.avgRk * 100).toFixed(0)}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {overallAverages.avgCw !== null
                        ? `${(overallAverages.avgCw * 100).toFixed(0)}%`
                        : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      {overallAverages.avgTotal !== null
                        ? `${(overallAverages.avgTotal * 100).toFixed(0)}%`
                        : "-"}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="card-job-info">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Briefcase className="w-4 h-4" />
            Informações da Vaga
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Hiring Manager</p>
              <p
                className="font-medium flex items-center gap-1.5"
                data-testid="text-hiring-manager"
              >
                <User className="w-4 h-4 text-muted-foreground" />
                {hiringManagerName}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Salário Esperado</p>
              <p
                className="font-medium flex items-center gap-1.5"
                data-testid="text-expected-salary"
              >
                <DollarSign className="w-4 h-4 text-muted-foreground" />
                {formatCurrency(details.jobOpening?.salaryExpected)}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Departamento</p>
              <p className="font-medium" data-testid="text-department">
                {details.jobOpening?.department || "-"}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Nível</p>
              <p className="font-medium" data-testid="text-job-level">
                {details.jobOpening?.jobLevel || "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {(details.referenceChecks?.length ?? 0) > 0 && (
        <Card data-testid="card-reference-checks">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="w-4 h-4" />
              Reference Checks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1">
              {details.referenceChecks!.map((rc) => {
                const hasAnswers = rc.answers?.some((a) => a.answer.trim().length > 0);
                return (
                  <div
                    key={rc.id}
                    className="flex-shrink-0 w-56 rounded-lg border border-border bg-card p-3 flex flex-col gap-2"
                  >
                    <p className="text-sm font-medium leading-tight line-clamp-2">
                      {rc.referrerName}
                    </p>
                    <Badge
                      className={
                        hasAnswers
                          ? "bg-stone-100 text-stone-600 dark:bg-stone-800/40 dark:text-stone-400 hover:bg-stone-100 w-fit text-xs"
                          : "bg-muted text-muted-foreground hover:bg-muted w-fit text-xs"
                      }
                    >
                      {hasAnswers ? "Respondido" : "Pendente"}
                    </Badge>
                    {rc.referrerLinkedin && (
                      <a
                        href={rc.referrerLinkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 truncate"
                      >
                        <Linkedin className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">LinkedIn</span>
                      </a>
                    )}
                    {rc.referrerPhone && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                        <Phone className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{rc.referrerPhone}</span>
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-auto text-xs h-7"
                      onClick={() => setViewingRc(rc)}
                      disabled={!hasAnswers}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Ver respostas
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!viewingRc} onOpenChange={(open) => !open && setViewingRc(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{viewingRc?.referrerName}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-1 pb-2 flex-1 space-y-5">
            {viewingRc && (viewingRc.answers?.length ?? 0) > 0 ? (
              viewingRc
                .answers!.filter((a) => a.answer.trim().length > 0)
                .map((item) => (
                  <div key={item.question} className="space-y-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {item.question}
                    </p>
                    <p className="text-sm">{item.answer}</p>
                  </div>
                ))
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma resposta registrada.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Card data-testid="card-detailed-feedback">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="w-4 h-4" />
            Avaliação Detalhada
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {completedInterviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum feedback detalhado disponível.</p>
          ) : (
            completedInterviews
              .sort((a, b) => (a.stage?.stageOrder || 0) - (b.stage?.stageOrder || 0))
              .map((interview, index) => (
                <div key={interview.id} data-testid={`section-feedback-${interview.id}`}>
                  {index > 0 && <Separator className="mb-6" />}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3
                        className="font-semibold text-base"
                        data-testid={`text-interviewer-name-${interview.id}`}
                      >
                        {interview.interviewer?.fullName || "Entrevistador"}
                      </h3>
                      <Badge variant="secondary" className="text-xs">
                        {interview.stage?.stageName || "Etapa"}
                      </Badge>
                      {interview.recommendation === "advance" ? (
                        <Badge
                          variant="outline"
                          className="border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400 text-xs"
                        >
                          Avançar
                        </Badge>
                      ) : interview.recommendation === "reject" ? (
                        <Badge
                          variant="outline"
                          className="border-stone-300 text-stone-600 dark:border-stone-600 dark:text-stone-400 text-xs"
                        >
                          Rejeitar
                        </Badge>
                      ) : null}
                    </div>

                    {(interview.customCriteriaScores?.length ?? 0) > 0 ? (
                      <div className="grid grid-cols-1 gap-4">
                        {interview.customCriteriaScores!.map((s) => (
                          <div key={s.criterionId} className="bg-muted/50 rounded-md p-3 space-y-2">
                            <div className="flex items-center gap-1.5 text-sm font-medium">
                              <ClipboardList className="w-3.5 h-3.5" />
                              {s.label}
                              <span className="ml-auto text-[13px] font-semibold">
                                {formatCriterionScore(s)}
                              </span>
                            </div>
                            {s.notes && (
                              <p
                                className="text-sm text-muted-foreground whitespace-pre-wrap"
                                data-testid={`text-criterion-notes-${interview.id}-${s.criterionId}`}
                              >
                                {s.notes}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4">
                        <div className="bg-muted/50 rounded-md p-3 space-y-2">
                          <div className="flex items-center gap-1.5 text-sm font-medium">
                            <Brain className="w-3.5 h-3.5" />
                            GCA
                            <span className="ml-auto">
                              <VerdictDisplay verdict={interview.gcaVerdict} />
                            </span>
                          </div>
                          {interview.gcaNotes && (
                            <p
                              className="text-sm text-muted-foreground whitespace-pre-wrap"
                              data-testid={`text-gca-notes-${interview.id}`}
                            >
                              {interview.gcaNotes}
                            </p>
                          )}
                        </div>

                        <div className="bg-muted/50 rounded-md p-3 space-y-2">
                          <div className="flex items-center gap-1.5 text-sm font-medium">
                            <BookOpen className="w-3.5 h-3.5" />
                            Role Knowledge
                            <span className="ml-auto">
                              <VerdictDisplay verdict={interview.roleKnowledgeVerdict} />
                            </span>
                          </div>
                          {interview.roleKnowledgeNotes && (
                            <p
                              className="text-sm text-muted-foreground whitespace-pre-wrap"
                              data-testid={`text-rk-notes-${interview.id}`}
                            >
                              {interview.roleKnowledgeNotes}
                            </p>
                          )}
                        </div>

                        <div className="bg-muted/50 rounded-md p-3 space-y-2">
                          <div className="flex items-center gap-1.5 text-sm font-medium">
                            <Heart className="w-3.5 h-3.5" />
                            COMP Way
                            <span className="ml-auto">
                              <VerdictDisplay verdict={interview.compWayVerdict} />
                            </span>
                          </div>
                          {interview.compWayNotes && (
                            <p
                              className="text-sm text-muted-foreground whitespace-pre-wrap"
                              data-testid={`text-cw-notes-${interview.id}`}
                            >
                              {interview.compWayNotes}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {interview.generalComments && (
                      <div className="bg-muted/50 rounded-md p-3 space-y-1">
                        <div className="flex items-center gap-1.5 text-sm font-medium">
                          <MessageSquare className="w-3.5 h-3.5" />
                          Comentários Gerais
                        </div>
                        <p
                          className="text-sm text-muted-foreground whitespace-pre-wrap"
                          data-testid={`text-general-comments-${interview.id}`}
                        >
                          {interview.generalComments}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
